import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { changePassword, type ChangePasswordPayload, type MeResponse } from '../lib/api'

type Props = {
  open: boolean
  onClose?: () => void  // omitted/disabled if forced (must_change_password)
  forced?: boolean
}

export function ChangePasswordModal({ open, onClose, forced = false }: Props) {
  const qc = useQueryClient()
  const [current, setCurrent] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const mut = useMutation<MeResponse, Error, ChangePasswordPayload>({
    mutationFn: (body) => changePassword(body),
    onSuccess: () => {
      setSuccess(true)
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] })
      // Auto-close after 1.5s
      setTimeout(() => {
        if (onClose) onClose()
      }, 1500)
    },
  })

  if (!open) return null

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (newPwd.length < 8) {
      setError('La nuova password deve essere almeno 8 caratteri')
      return
    }
    if (newPwd !== confirm) {
      setError('Le password non coincidono')
      return
    }
    if (newPwd === current) {
      setError('La nuova password deve essere diversa da quella attuale')
      return
    }
    mut.mutate({ current_password: current, new_password: newPwd })
  }

  function onOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (forced) return
    if (e.target === e.currentTarget && onClose) onClose()
  }

  return (
    <div className="modal-overlay" onClick={onOverlayClick}>
      <div className="modal-content" style={{ maxWidth: 440 }}>
        {!forced && onClose && (
          <button
            type="button"
            className="modal-close"
            aria-label="Chiudi"
            onClick={onClose}
          >
            ×
          </button>
        )}
        <div className="modal-body">
          <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: 18 }}>
            {forced ? 'Cambia password (obbligatorio)' : 'Cambia password'}
          </h2>
          {forced && (
            <p className="muted" style={{ marginBottom: 16 }}>
              Per motivi di sicurezza devi cambiare la password al primo login.
            </p>
          )}
          {error && <div className="error">{error}</div>}
          {success && (
            <div className="banner-success">Password aggiornata.</div>
          )}
          {mut.isError && !error && (
            <div className="error">{mut.error?.message ?? 'Errore'}</div>
          )}
          <form onSubmit={onSubmit}>
            <div className="form-field">
              <label>Password attuale *</label>
              <input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div className="form-field">
              <label>Nuova password * (min 8 caratteri)</label>
              <input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            <div className="form-field">
              <label>Conferma nuova password *</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
                marginTop: 16,
              }}
            >
              {!forced && onClose && (
                <button
                  type="button"
                  className="secondary"
                  onClick={onClose}
                  disabled={mut.isPending}
                >
                  Annulla
                </button>
              )}
              <button type="submit" disabled={mut.isPending || success}>
                {mut.isPending ? 'Salvataggio…' : 'Conferma'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
